/**
 * @picnic/mech-kinematics — 모터 각으로 관절 각을 푸는 K층(관절 해석 · 한계 · 풀이). three 를 모른다.
 *
 * Bench 조립 모형 보기(`apps/bench-web/src/features/sim/lib/board3d/build.ts` · `createBuildView`)와 Field 커널이 <b>이 한 벌</b>로 푼다
 * (FIELD-CRQ-001 D-3 · FIELD-DEC-001 "같은 기계, 같은 운동학"). build.ts 는 장면을 읽어 관절 원문을 넘기고, 푼 값을 노드에 옮기기만 한다.
 * 이 파일은 원래 build.ts 안에 있던 풀이부를 PoC P1(`poc-final:poc/p1-kinematics/src/kinematics.ts`)을 거쳐 옮긴 것이다 —
 * 풀이 규칙은 한 줄도 바꾸지 않고, three 에 기대던 것만 걷어 냈다:
 *   - `THREE.Vector3` 축 → `{x,y,z}` (정규화 산식은 three 와 같게: x * (1 / length))
 *   - `THREE.MathUtils.degToRad/radToDeg` → 같은 상수(π/180, 180/π)의 곱
 *   - 장면 노드 회전·평행 이동 → 없음(값만 돌려준다. 그리는 것은 부르는 쪽 몫)
 *   - 초월함수 `Math.sin`·`Math.hypot` → 결정론 수학(`./det-math` — CRQ-001 D-4)
 *
 * 계약(CRQ-001 R-3): 입력 = GLB `extras.joint` 원문 목록(**장면 전위 순회 순서**) · 클립 길이(초) · 관리자 한계 덮어쓰기.
 * 출력 = `setMotors(angles)` → `solved()`(turn·slide) · `offsets()` · `blocked()` · `portLimits()` · `setOverrides()`.
 * 기준: test/fixtures/k-match(43종 · 3 시퀀스 — Bench 62f4f03 화면에 DET 를 넣어 뽑은 값)와 **비트 단위로 같다**(test/k-golden.test.ts).
 */
import {
  hold,
  overridden,
  spreadLimits,
  statedLimit,
  type Limit,
  type LimitOverride,
} from "./limits";
import {
  makeDyad,
  makeLink,
  makeRocker,
  makeSlider,
  type Dyad,
  type Link,
  type Rocker,
  type Slider,
  type Vec3,
} from "./linkage";
import { rrAngle, trackAngle, trackOffset, unwrap, type ChainJoint, type Pose } from "./chain";
import { cycleAngle, cyclesWithCrank, makeWave } from "./wave";
import { hypot, sin } from "./det-math";

// three MathUtils 와 같은 상수 — 곱셈 한 번(나눗셈으로 바꾸면 끝자리가 달라진다)
const DEG2RAD = Math.PI / 180;
const RAD2DEG = 180 / Math.PI;
const rad = (d: number) => d * DEG2RAD;
const deg2 = (r: number) => r * RAD2DEG;

type Axis = { x: number; y: number; z: number };

/** 굽는 스크립트가 GLB extras 에 실은 관절 원문(build.ts 의 Raw 와 같은 모양) */
export type RawJoint = {
  id?: string;
  port?: string | null;
  axis?: number[];
  type?: string;
  pivot_cm?: number[];
  parent?: string | null;
  motor?: string | null;
  follow?: {
    joint?: string;
    ratio?: number;
    mode?: string;
    pin?: number[];
    attach?: number[];
    guide?: number[];
    rocker?: string;
    scale?: number[];
    radius?: number;
    top?: number[];
    pinAxis?: number[];
    part?: string;
    other?: string;
    otherPin?: number[];
    joint2?: number[];
  } | null;
  lift?: { joint?: string; radius?: number; ratio?: number; abs?: boolean; half?: string } | null;
  spin?: unknown;
  swing?: unknown;
  travel?: unknown;
  limit?: { min?: number; max?: number } | null;
  /** 판 기울기 규칙(stage-tilt · 2026-10-07) — 풀이는 보지 않는다. 부르는 쪽이 `parseTiltSpec` 으로 읽는다 */
  tilt?: unknown;
  /**
   * 모터 방향(1.2.0 · 2026-10-07) — 실물 허브의 모터 + (회전판을 바라볼 때 시계 방향)가 이 관절 축의 + 면 1, − 면 −1.
   * 굽는 스크립트가 모터 몸통 · 회전판 자리로 재서 싣는다. 없으면 1(그 전 GLB — 예전과 같이 푼다).
   */
  motorDir?: number;
};

/** 관절의 모터 방향 — −1 만 −1, 그 밖(없음 · 1 · 이상한 값)은 1 */
export const motorDirOf = (j: { motorDir?: unknown } | null | undefined): 1 | -1 =>
  j?.motorDir === -1 ? -1 : 1;

type Drive =
  | { kind: "ratio"; joint: string; ratio: number }
  | { kind: "rocker"; joint: string; solve: Rocker; slides: boolean }
  | { kind: "conrod"; rocker: string }
  | { kind: "slider"; joint: string; solve: Slider }
  | { kind: "track"; joint: string; pin: Vec3; scale: Vec3 }
  | { kind: "rr"; joint: string; pin: Vec3; attach: Vec3 }
  | {
      kind: "lift";
      joint: string;
      radius: number;
      ratio: number;
      abs: boolean;
      half: string | null;
    }
  | { kind: "rack"; joint: string; radius: number; ratio: number }
  | { kind: "link"; joint: string; rocker: string; part: "swivel" | "pitch"; solve: Link }
  | { kind: "dyad"; joint: string; other: string; solve: Dyad }
  | { kind: "cycle"; joint: string; spin: number; wave: (t: number) => number };

type Spec = {
  id: string;
  axis: Axis;
  port: string | null;
  type: string;
  prismatic: boolean;
  follow: { joint: string; ratio: number } | null;
  drive: Drive | null;
  limit: Limit | null;
  base: Limit | null;
  /** 모터 각 → 이 관절 각의 부호({@link motorDirOf}) — 포트 관절만 쓴다 */
  dir: 1 | -1;
  raw: RawJoint;
};

export type Solved = { turn: Record<string, number>; slide: Record<string, number> };

export type Mechanism = {
  /** 포트별 누적 모터 각(도)을 받아 푼다 — Bench `setMotors` 와 같은 상태 전이 */
  setMotors: (angles: Record<string, number>) => void;
  solved: () => Solved;
  blocked: () => string[];
  portLimits: () => Record<string, Limit | null>;
  setOverrides: (o: Record<string, LimitOverride>) => void;
  /** 관절 id 목록(해석 순서) */
  joints: () => string[];
  /**
   * 미끄러지는 관절이 제자리에서 옮겨 간 만큼(스펙 cm · 축 방향 벡터) — 부르는 쪽이 장면 노드를 옮길 때 쓴다(Bench build.ts).
   * 값은 {@link solved} 의 slide 와 같은 풀이에서 나온다. 돌려준 표는 사본이다.
   */
  offsets: () => Record<string, Vec3>;
};

/**
 * 관절 스펙으로 기구를 세운다.
 * @param raws 관절 원문 — **GLB 장면 순회 순서 그대로**(순서가 풀이 순서다)
 * @param clipDuration 구운 클립 길이(초) — `cycle` 모드 판정에 쓴다(Bench 는 gltf.animations[0].duration)
 * @param overrides 관리자 한계 덮어쓰기(Campus motor-limits). 없으면 {}
 */
export function createMechanism(
  raws: RawJoint[],
  clipDuration: number,
  overrides: Record<string, LimitOverride> = {},
): Mechanism {
  const joints = new Map<string, Spec>();
  for (const j of raws) {
    if (!j?.id || !j.axis) continue;
    const ax = j.axis[0] as number,
      ay = j.axis[1] as number,
      az = j.axis[2] as number;
    if (ax * ax + ay * ay + az * az < 1e-9) continue; // three lengthSq() 와 같은 판정
    const len = Math.sqrt(ax * ax + ay * ay + az * az);
    const inv = 1 / (len || 1); // three normalize(): divideScalar(len||1) = multiplyScalar(1/…)
    const f =
      j.follow && !j.follow.mode && j.follow.joint && Number.isFinite(j.follow.ratio)
        ? { joint: j.follow.joint, ratio: j.follow.ratio as number }
        : null;
    joints.set(j.id, {
      id: j.id,
      axis: { x: ax * inv, y: ay * inv, z: az * inv },
      port: j.port ?? null,
      type: j.type ?? "revolute",
      prismatic: j.type === "prismatic",
      follow: f,
      drive: f ? { kind: "ratio", ...f } : null,
      limit: statedLimit(j),
      base: statedLimit(j),
      dir: motorDirOf(j),
      raw: j,
    });
  }

  const spread = () => {
    const turning = [...joints.values()].filter((j) => !j.prismatic);
    for (const [id, lim] of spreadLimits(turning)) (joints.get(id) as Spec).limit = lim;
  };

  const linkSolvers = new Map<string, Link>();
  const v3 = (a: number[] | undefined): Vec3 | null =>
    a && a.length === 3 && a.every(Number.isFinite)
      ? [a[0] as number, a[1] as number, a[2] as number]
      : null;
  for (const j of joints.values()) {
    const f = j.raw.follow;
    if (!f?.mode) continue;
    const crank = f.joint ? joints.get(f.joint) : undefined;
    const axis = v3(j.raw.axis),
      pivot = v3(j.raw.pivot_cm);
    const cAxis = v3(crank?.raw.axis),
      cPivot = v3(crank?.raw.pivot_cm);
    const pin = v3(f.pin),
      attach = v3(f.attach);
    try {
      if (f.mode === "rocker" && axis && pivot && cAxis && cPivot && pin && attach && crank) {
        j.drive = {
          kind: "rocker",
          joint: crank.id,
          slides: crank.prismatic,
          solve: makeRocker({
            axis,
            pivot,
            crankAxis: cAxis,
            crankPivot: cPivot,
            pin,
            attach,
            crankSlides: crank.prismatic,
          }),
        };
      } else if (f.mode === "conrod" && f.rocker && joints.has(f.rocker)) {
        j.drive = { kind: "conrod", rocker: f.rocker };
      } else if (f.mode === "slider" && axis && cAxis && cPivot && pin && attach && crank) {
        j.drive = {
          kind: "slider",
          joint: crank.id,
          solve: makeSlider({
            axis,
            crankAxis: cAxis,
            crankPivot: cPivot,
            pin,
            attach,
            guide: v3(f.guide) ?? [0, 1, 0],
          }),
        };
      } else if (f.mode === "track" && crank && pin) {
        j.drive = { kind: "track", joint: crank.id, pin, scale: v3(f.scale) ?? [1, 1, 1] };
      } else if (f.mode === "rr" && crank && pin && attach) {
        j.drive = { kind: "rr", joint: crank.id, pin, attach };
      } else if (
        f.mode === "rack" &&
        crank &&
        crank.prismatic &&
        Number.isFinite(f.radius) &&
        f.radius
      ) {
        j.drive = {
          kind: "rack",
          joint: crank.id,
          radius: f.radius as number,
          ratio: f.ratio ?? 1,
        };
      } else if (f.mode === "link" && crank && (f.part === "swivel" || f.part === "pitch")) {
        const rk = f.rocker ? joints.get(f.rocker) : undefined;
        const top = v3(f.top),
          pinAxis = v3(f.pinAxis);
        const rAxis = v3(rk?.raw.axis),
          rPivot = v3(rk?.raw.pivot_cm);
        if (rk && cAxis && cPivot && rAxis && rPivot && pin && top && attach && pinAxis) {
          const id = `${crank.id}|${rk.id}|${pin.join(",")}`;
          let solve = linkSolvers.get(id);
          if (!solve) {
            solve = makeLink({
              crankAxis: cAxis,
              crankPivot: cPivot,
              rockerAxis: rAxis,
              rockerPivot: rPivot,
              pin,
              top,
              attach,
              pinAxis,
            });
            linkSolvers.set(id, solve);
          }
          j.drive = { kind: "link", joint: crank.id, rocker: rk.id, part: f.part, solve };
        }
      } else if (f.mode === "cycle" && crank) {
        const spin = Number(crank.raw.spin),
          clip = clipDuration ?? 0;
        if (cyclesWithCrank(j.raw.swing, spin, clip)) {
          j.drive = {
            kind: "cycle",
            joint: crank.id,
            spin,
            wave: makeWave(j.raw.swing as never, clip),
          };
        }
      } else if (f.mode === "dyad" && crank && f.other && joints.has(f.other)) {
        const other = joints.get(f.other) as Spec;
        const oPivot = v3(other.raw.pivot_cm),
          otherPin = v3(f.otherPin),
          joint2 = v3(f.joint2);
        if (axis && cPivot && oPivot && pin && otherPin && joint2) {
          j.drive = {
            kind: "dyad",
            joint: crank.id,
            other: other.id,
            solve: makeDyad({
              axis,
              crankPivot: cPivot,
              otherPivot: oPivot,
              pin,
              otherPin,
              joint2,
            }),
          };
        }
      }
    } catch {
      j.drive = null;
    }
  }
  for (const j of joints.values()) {
    const lf = j.raw.lift;
    if (j.drive || !lf?.joint || !joints.has(lf.joint) || !Number.isFinite(lf.radius)) continue;
    j.drive = {
      kind: "lift",
      joint: lf.joint,
      radius: lf.radius as number,
      ratio: lf.ratio ?? 1,
      abs: !!lf.abs,
      half: lf.half ?? null,
    };
  }

  /** 관리자 한계(모터 각) → 관절 각 — 방향이 −1 이면 뒤집는다 */
  const toJoint = (o: LimitOverride, dir: 1 | -1): LimitOverride =>
    dir > 0 || o.unlimited || o.min === null || o.max === null
      ? o
      : { unlimited: false, min: -o.max, max: -o.min };

  const gearedToMotor = (j: Spec) => !!(j.follow && joints.get(j.follow.joint)?.port === j.port);

  const applyLimits = (o: Record<string, LimitOverride>) => {
    for (const j of joints.values()) {
      j.limit = j.base;
      if (j.port && !j.prismatic && o[j.port] && !gearedToMotor(j))
        j.limit = overridden(j.limit, toJoint(o[j.port] as LimitOverride, j.dir));
    }
    spread();
    let added = false;
    for (const j of joints.values()) {
      const d = j.drive;
      if (d?.kind !== "rack") continue;
      const rail = joints.get(d.joint)?.limit;
      if (!rail || !d.ratio) continue;
      const k = deg2(d.ratio / d.radius);
      const a = rail.min * k,
        b = rail.max * k;
      const lo = Math.min(a, b),
        hi = Math.max(a, b);
      j.limit = j.limit
        ? { min: Math.max(j.limit.min, lo), max: Math.min(j.limit.max, hi) }
        : { min: lo, max: hi };
      added = true;
    }
    if (added) spread();
  };
  applyLimits(overrides);

  const movable = new Set<string>();
  {
    for (const j of joints.values()) if (j.port) movable.add(j.id);
    for (let pass = 0; pass < joints.size; pass++) {
      let grew = false;
      for (const j of joints.values()) {
        const d = j.drive;
        if (!d) continue;
        const src = d.kind === "conrod" ? d.rocker : d.joint;
        const also = d.kind === "link" ? d.rocker : d.kind === "dyad" ? d.other : null;
        if ((movable.has(src) || (also && movable.has(also))) && !movable.has(j.id)) {
          movable.add(j.id);
          grew = true;
        }
        if (
          (d.kind === "ratio" || d.kind === "rack") &&
          movable.has(j.id) &&
          !movable.has(d.joint)
        ) {
          movable.add(d.joint);
          grew = true;
        }
      }
      if (!grew) break;
    }
  }

  const portOfJoint = new Map<string, string>();
  {
    const seek = (id: string, seen: Set<string>): string | null => {
      const j = joints.get(id);
      if (!j || seen.has(id)) return null;
      if (j.port) return j.port;
      seen.add(id);
      const d = j.drive;
      if (!d) return null;
      for (const up of d.kind === "conrod"
        ? [d.rocker]
        : d.kind === "link"
          ? [d.joint, d.rocker]
          : d.kind === "dyad"
            ? [d.joint, d.other]
            : [d.joint]) {
        const p = seek(up, seen);
        if (p) return p;
      }
      return null;
    };
    for (const id of joints.keys()) {
      const p = seek(id, new Set());
      if (p) portOfJoint.set(id, p);
    }
  }
  const portDriving = (id: string) => portOfJoint.get(id) ?? null;

  const bones = new Map<string, ChainJoint>();
  for (const j of joints.values()) {
    const axis = v3(j.raw.axis),
      pivot = v3(j.raw.pivot_cm);
    if (!axis || !pivot) continue;
    bones.set(j.id, {
      id: j.id,
      parent: j.raw.parent ?? null,
      prismatic: j.prismatic,
      axis,
      pivot,
    });
  }

  const applied = new Map<string, number>();
  let blockedPorts: string[] = [];
  const slidTo = new Map<string, Vec3>();
  const lastTheta = new Map<string, number>();
  const lastPhi = new Map<string, number>();
  const lastSlide = new Map<string, number>();

  const solve = (
    motors: Record<string, number>,
  ): { angles: Map<string, number>; offsets: Map<string, Vec3> } => {
    const known = new Map<string, number>();
    const slid = new Map<string, Vec3>();
    for (const j of joints.values()) {
      if (movable.has(j.id)) continue;
      if (j.prismatic) slid.set(j.id, [0, 0, 0]);
      else known.set(j.id, 0);
    }
    const pose: Pose = {
      angle: (id) => {
        const j = joints.get(id);
        if (!j || j.prismatic) return 0;
        const v = known.get(id);
        return v === undefined ? undefined : rad(v);
      },
      offset: (id) => (joints.get(id)?.prismatic ? slid.get(id) : [0, 0, 0]),
    };
    const gearedToSameMotor = (j: Spec) =>
      j.drive?.kind === "ratio" && joints.get(j.drive.joint)?.port === j.port;
    const stuck = new Set<string>();
    for (const j of joints.values()) {
      if (j.prismatic || gearedToSameMotor(j)) continue;
      const m = j.port ? motors[j.port] : undefined;
      // 모터 각 → 관절 각(motorDir · 1.2.0). 1 이면 곱하지 않는다 — 그 전 GLB 는 예전 비트 그대로
      const deg = m !== undefined && j.dir < 0 ? -m : m;
      if (!Number.isFinite(deg)) continue;
      const held = hold(deg as number, j.limit);
      if (j.port && Math.abs(held - (deg as number)) > 1e-6) stuck.add(j.port);
      known.set(j.id, held);
    }

    const travelled = (id: string): number | undefined => {
      const v = slid.get(id),
        a = joints.get(id)?.axis;
      return v && a ? v[0] * a.x + v[1] * a.y + v[2] * a.z : undefined;
    };

    const rocker = (j: Spec, d: Drive & { kind: "rocker" }): number | undefined => {
      const drive = d.slides ? travelled(d.joint) : known.get(d.joint);
      if (drive === undefined) return undefined;
      const phi = d.slides ? drive : rad(drive);
      const th = d.solve.theta(phi, lastTheta.get(j.id) ?? 0, lastPhi.get(j.id) ?? 0);
      if (th === null) return deg2(lastTheta.get(j.id) ?? 0);
      lastTheta.set(j.id, th);
      lastPhi.set(j.id, phi);
      return deg2(th);
    };

    const done = (j: Spec) => (j.prismatic ? slid.has(j.id) : known.has(j.id));

    for (let pass = 0; pass < joints.size; pass++) {
      let grew = false;
      for (const j of joints.values()) {
        const d = j.drive;
        if (!d || done(j)) {
          if (d?.kind === "ratio" && known.has(j.id) && !known.has(d.joint) && d.ratio !== 0) {
            const other = joints.get(d.joint);
            if (other) {
              known.set(other.id, hold((known.get(j.id) as number) / d.ratio, other.limit));
              grew = true;
            }
          }
          if (d?.kind === "rack" && known.has(j.id) && !slid.has(d.joint) && d.ratio !== 0) {
            const rail = joints.get(d.joint);
            if (rail) {
              const dist = hold((rad(known.get(j.id) as number) * d.radius) / d.ratio, rail.limit);
              slid.set(rail.id, [rail.axis.x * dist, rail.axis.y * dist, rail.axis.z * dist]);
              grew = true;
            }
          }
          continue;
        }
        let got: number | undefined, vec: Vec3 | undefined;
        if (d.kind === "track") {
          if (j.prismatic) {
            const v = trackOffset(bones, j.id, d.joint, d.pin, pose, d.scale);
            if (v) vec = v;
          } else {
            const a = trackAngle(bones, j.id, d.joint, d.pin, pose);
            if (a !== null) {
              const th = unwrap(a, lastTheta.get(j.id) ?? 0);
              lastTheta.set(j.id, th);
              got = deg2(th);
            }
          }
        } else if (d.kind === "rr") {
          const a = rrAngle(bones, j.id, d.joint, d.pin, d.attach, pose);
          if (a !== null) {
            const th = unwrap(a, lastTheta.get(j.id) ?? 0);
            lastTheta.set(j.id, th);
            got = deg2(th);
          } else if (lastTheta.has(j.id)) got = deg2(lastTheta.get(j.id) as number);
        } else if (d.kind === "lift") {
          const src = known.get(d.joint);
          if (src !== undefined) {
            const sn = sin(d.ratio * rad(src));
            const h = d.half ? Math.max(0, d.half === "neg" ? -sn : sn) : d.abs ? Math.abs(sn) : sn;
            const dist = d.radius * h;
            vec = [j.axis.x * dist, j.axis.y * dist, j.axis.z * dist];
          }
        } else if (d.kind === "link") {
          const th = known.get(d.joint),
            ph = known.get(d.rocker);
          if (th !== undefined && ph !== undefined) {
            const r = d.solve(rad(th), rad(ph));
            if (r) got = deg2(d.part === "pitch" ? r.pitch : r.swivel);
            else if (lastTheta.has(j.id)) got = deg2(lastTheta.get(j.id) as number);
            if (r) lastTheta.set(j.id, d.part === "pitch" ? r.pitch : r.swivel);
          }
        } else if (d.kind === "dyad") {
          const mine = known.get(d.joint);
          const oth = known.get(d.other);
          if (mine !== undefined && oth !== undefined) {
            const a = d.solve(rad(mine), rad(oth));
            if (a !== null) {
              const th = unwrap(a, lastTheta.get(j.id) ?? 0);
              lastTheta.set(j.id, th);
              got = deg2(th);
            } else if (lastTheta.has(j.id)) got = deg2(lastTheta.get(j.id) as number);
          }
        } else if (d.kind === "cycle") {
          const th = known.get(d.joint);
          if (th !== undefined) got = cycleAngle(d.wave, th, d.spin);
        } else if (d.kind === "rack") {
          const dist = travelled(d.joint);
          if (dist !== undefined) got = hold(deg2((d.ratio * dist) / d.radius), j.limit);
        } else if (d.kind === "ratio") {
          const theirs = known.get(d.joint);
          if (theirs !== undefined) got = hold(theirs * d.ratio, j.limit);
        } else if (d.kind === "rocker") {
          got = rocker(j, d);
        } else if (d.kind === "conrod") {
          const rk = joints.get(d.rocker);
          const rd = rk?.drive;
          if (rk && rd?.kind === "rocker") {
            const th = known.get(rk.id) ?? rocker(rk, rd);
            const drive = rd.slides ? travelled(rd.joint) : known.get(rd.joint);
            if (th !== undefined && drive !== undefined) {
              got = deg2(rd.solve.conrod(rd.slides ? drive : rad(drive), rad(th)));
            }
          }
        } else if (d.kind === "slider") {
          const phiDeg = known.get(d.joint);
          if (phiDeg !== undefined) {
            const r = d.solve(rad(phiDeg), lastSlide.get(j.id) ?? 0);
            if (r) {
              lastSlide.set(j.id, r.slide);
              lastTheta.set(j.id, r.angle);
              got = deg2(r.angle);
            } else got = deg2(lastTheta.get(j.id) ?? 0);
          }
        }
        if (got !== undefined) {
          if (j.limit && Math.abs(got - hold(got, j.limit)) > 1e-6) {
            const p = portDriving(j.id);
            if (p) stuck.add(p);
          }
          known.set(j.id, got);
          grew = true;
        }
        if (vec !== undefined) {
          slid.set(j.id, vec);
          grew = true;
        }
      }
      if (!grew) break;
    }
    blockedPorts = [...stuck].sort();
    return { angles: known, offsets: slid };
  };

  let lastMotors: Record<string, number> = {};

  const mech: Mechanism = {
    setMotors(angles) {
      lastMotors = angles;
      const { angles: solved, offsets } = solve(angles);
      // 바뀐 것만 applied/slidTo 에 적는다 — Bench 화면이 바뀐 것만 장면에 옮기던 기록 규칙 그대로
      for (const [id, deg] of solved) {
        const j = joints.get(id);
        if (!j || j.prismatic || applied.get(id) === deg) continue;
        applied.set(id, deg);
      }
      for (const [id, v] of offsets) {
        if (!joints.get(id)) continue;
        const was = slidTo.get(id);
        if (was && was[0] === v[0] && was[1] === v[1] && was[2] === v[2]) continue;
        slidTo.set(id, v);
      }
    },
    solved: () => ({
      turn: Object.fromEntries(applied),
      slide: Object.fromEntries(
        [...slidTo].map(([id, v]) => {
          const a = joints.get(id)?.axis;
          return [id, a ? v[0] * a.x + v[1] * a.y + v[2] * a.z : hypot(v[0], v[1], v[2])];
        }),
      ),
    }),
    blocked: () => blockedPorts,
    portLimits() {
      const out: Record<string, Limit | null> = {};
      for (const j of joints.values()) {
        if (!j.port || j.prismatic || gearedToMotor(j) || j.port in out) continue;
        // 모터 각으로 돌려준다(미리보기 슬라이더 · 관리자 화면) — 방향이 −1 이면 뒤집는다
        out[j.port] = j.limit && j.dir < 0 ? { min: -j.limit.max, max: -j.limit.min } : j.limit;
      }
      return out;
    },
    setOverrides(o) {
      applyLimits(o);
      mech.setMotors(lastMotors);
    },
    joints: () => [...joints.keys()],
    offsets: () => Object.fromEntries(slidTo),
  };
  return mech;
}
